import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'react-toastify'
import { FiArrowRight, FiMapPin, FiPhone } from 'react-icons/fi'
import { Container, Reveal, HoverText, CircleBadge, Select } from '@/components/common'
import { sendContactEmail } from '@/lib/emailjs'
import { contactFormSchema } from '@/utils'
import { cn } from '@/lib/cn'
import {
  interests,
  details,
  ukBranch,
  mapEmbedUrl,
  fieldClass,
  labelClass,
} from '@/constants/contact'

const errorFieldClass = 'border-red-500 focus:border-red-500'

export const Contact = () => {
  const [searchParams] = useSearchParams()
  const prefilledInterest = searchParams.get('interest') ?? ''
  const initialInterest = interests.includes(prefilledInterest) ? prefilledInterest : ''

  const [sending, setSending] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [selectKey, setSelectKey] = useState(0)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)

    const result = contactFormSchema.safeParse({
      name: String(data.get('name') || ''),
      email: String(data.get('email') || ''),
      subject: String(data.get('subject') || ''),
      interest: String(data.get('interest') || ''),
      message: String(data.get('message') || ''),
    })

    if (!result.success) {
      setFieldErrors(
        Object.fromEntries(
          Object.entries(result.error.flatten().fieldErrors).map(([key, messages]) => [
            key,
            messages?.[0] ?? '',
          ]),
        ),
      )
      return
    }

    setFieldErrors({})
    setSending(true)

    try {
      await sendContactEmail(result.data)
      toast.success("Thanks, we'll be in touch soon.")
      form.reset()
      setSelectKey((key) => key + 1)
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <section id="contact" className="bg-white py-24 text-ibon-dark lg:py-32">
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <Reveal variant="up" delay={100}>
            <h2 className="mt-7 text-4xl font-bold leading-[1.02] tracking-[-0.02em] text-ibon-dark sm:text-5xl lg:text-6xl">
              Let&rsquo;s create opportunity <span className="text-ibon-dark/30">together.</span>
            </h2>
          </Reveal>
          <Reveal variant="up" delay={160}>
            <p className="mt-6 text-lg leading-relaxed text-text-muted">
              Whether you want to partner, collaborate, or simply learn more about our work across
              Africa and the Caribbean, we would love to hear from you.
            </p>
          </Reveal>
        </div>

        <Reveal variant="up" delay={140}>
          <form onSubmit={handleSubmit} className="relative mx-auto mt-16 max-w-4xl">
            <CircleBadge className="absolute -right-12 top-20 z-10 hidden xl:grid" />

            <div className="grid gap-x-10 gap-y-8 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className={labelClass}>
                  Your Name <span className="text-ibon-sky">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  placeholder="Enter your name"
                  className={cn(fieldClass, fieldErrors.name && errorFieldClass)}
                />
                {fieldErrors.name && (
                  <p className="mt-2 text-xs text-red-600">{fieldErrors.name}</p>
                )}
              </div>
              <div>
                <label htmlFor="contact-email" className={labelClass}>
                  Your Email <span className="text-ibon-sky">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  className={cn(fieldClass, fieldErrors.email && errorFieldClass)}
                />
                {fieldErrors.email && (
                  <p className="mt-2 text-xs text-red-600">{fieldErrors.email}</p>
                )}
              </div>
              <div>
                <label htmlFor="contact-subject" className={labelClass}>
                  Subject
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  placeholder="Subject"
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="contact-interest" className={labelClass}>
                  Area of Interest
                </label>
                <Select
                  key={selectKey}
                  id="contact-interest"
                  name="interest"
                  options={interests}
                  defaultValue={selectKey === 0 ? initialInterest : ''}
                  placeholder="Select an area of interest"
                />
              </div>
            </div>

            <div className="mt-8">
              <label htmlFor="contact-message" className={labelClass}>
                Your Message
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={6}
                placeholder="Enter your message"
                className={cn(
                  'w-full resize-y rounded-none border border-border bg-white px-4 py-3 text-ibon-dark transition-colors placeholder:text-text-muted focus:border-ibon-dark focus:outline-none',
                  fieldErrors.message && errorFieldClass,
                )}
              />
              {fieldErrors.message && (
                <p className="mt-2 text-xs text-red-600">{fieldErrors.message}</p>
              )}
            </div>

            <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="submit"
                disabled={sending}
                className="group inline-flex items-center gap-2 rounded-full bg-ibon-dark px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-ibon-sky hover:text-ibon-dark disabled:cursor-not-allowed disabled:opacity-60"
              >
                <HoverText text={sending ? 'Sending...' : 'Send Message'} />
                <FiArrowRight
                  className="transition-transform duration-300 group-hover:translate-x-1"
                  size={15}
                />
              </button>
            </div>
          </form>
        </Reveal>

        <Reveal variant="up" delay={150}>
          <div className="mx-auto mt-16 max-w-4xl border-2 border-ibon-sky bg-ibon-sky/10 p-8 sm:p-10">
            <span className="inline-flex items-center rounded-full bg-ibon-sky px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-ibon-dark">
              UK Branch
            </span>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <a
                href={ukBranch.mapHref}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-3 text-ibon-dark transition-colors hover:text-ibon-azure"
              >
                <FiMapPin className="mt-0.5 shrink-0 text-ibon-sky" size={20} />
                <span className="text-base font-medium leading-relaxed">{ukBranch.address}</span>
              </a>
              <a
                href={ukBranch.phoneHref}
                className="group flex items-center gap-3 text-ibon-dark transition-colors hover:text-ibon-azure"
              >
                <FiPhone className="shrink-0 text-ibon-sky" size={20} />
                <span className="text-base font-medium">{ukBranch.phone}</span>
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal variant="up" delay={160}>
          <div className="mx-auto mt-px grid max-w-4xl gap-px overflow-hidden border-t border-border sm:grid-cols-3">
            {details.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 border-b border-border bg-white py-6 text-sm text-text-muted sm:border-b-0 sm:py-8"
              >
                <Icon className="shrink-0 text-ibon-sky" size={18} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal variant="up" delay={200}>
          <div className="mx-auto mt-px max-w-4xl overflow-hidden border border-border">
            <iframe
              title="IBON World London office location"
              src={mapEmbedUrl}
              width="100%"
              height="360"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block w-full border-0 grayscale"
            />
          </div>
        </Reveal>
      </Container>
    </section>
  )
}
